import { useState } from 'react';
import { Sparkles, Target, Play, Trash2, ChevronDown, ChevronRight, MessageSquare } from 'lucide-react';

function SomedayItem({ item, onActivate, onDelete }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-surface-card rounded-lg border border-border hover:border-accent/30 transition-all">
      <div className="w-8 h-8 rounded-lg bg-surface-hover flex items-center justify-center flex-shrink-0">
        <Sparkles size={16} className="text-text-tertiary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-text-primary truncate">{item.title}</p>
        {item.notes && (
          <p className="text-xs text-text-tertiary truncate">{item.notes}</p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onActivate(item.id)}
          className="p-2 text-success hover:bg-success/10 rounded-lg transition-colors
                   border-none bg-transparent cursor-pointer"
          title="Activate this task"
        >
          <Play size={14} />
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="p-2 text-text-tertiary hover:text-error hover:bg-error/10 rounded-lg transition-colors
                   border-none bg-transparent cursor-pointer"
          title="Remove from someday"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function GoalCard({ goal }) {
  const statusColors = {
    active: 'bg-success/10 text-success',
    'on-hold': 'bg-warning/10 text-warning',
    completed: 'bg-text-tertiary/10 text-text-tertiary'
  };

  return (
    <div className="p-3 bg-surface-card rounded-lg border border-border">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-monthly/10 flex items-center justify-center flex-shrink-0">
          <Target size={16} className="text-monthly" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary">{goal.title}</p>
          {goal.description && (
            <p className="text-xs text-text-tertiary mt-1 line-clamp-2">{goal.description}</p>
          )}
          <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-2 ${statusColors[goal.status] || statusColors.active}`}>
            {goal.status || 'active'}
          </span>
        </div>
      </div>
    </div>
  );
}

function ReflectionSection({ reflection, onReflectionChange }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-surface-card rounded-xl border border-border overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left bg-transparent border-none cursor-pointer"
      >
        <div className="w-8 h-8 rounded-lg bg-monthly/10 flex items-center justify-center flex-shrink-0">
          <MessageSquare size={16} className="text-monthly" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-text-primary">Weekly Reflection</h3>
          <p className="text-xs text-text-tertiary">Optional - capture your thoughts</p>
        </div>
        {expanded ? <ChevronDown size={16} className="text-text-tertiary" /> : <ChevronRight size={16} className="text-text-tertiary" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              What went well this week?
            </label>
            <textarea
              value={reflection.wentWell}
              onChange={(e) => onReflectionChange({ ...reflection, wentWell: e.target.value })}
              placeholder="Wins, accomplishments, positive moments..."
              className="w-full px-3 py-2 text-sm bg-surface-base border border-border rounded-lg
                       resize-none focus:outline-none focus:border-accent text-text-primary
                       placeholder:text-text-tertiary"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              What could improve?
            </label>
            <textarea
              value={reflection.couldImprove}
              onChange={(e) => onReflectionChange({ ...reflection, couldImprove: e.target.value })}
              placeholder="Challenges, lessons learned, areas for growth..."
              className="w-full px-3 py-2 text-sm bg-surface-base border border-border rounded-lg
                       resize-none focus:outline-none focus:border-accent text-text-primary
                       placeholder:text-text-tertiary"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Focus for next week?
            </label>
            <textarea
              value={reflection.nextWeekFocus}
              onChange={(e) => onReflectionChange({ ...reflection, nextWeekFocus: e.target.value })}
              placeholder="Key priorities, intentions, goals..."
              className="w-full px-3 py-2 text-sm bg-surface-base border border-border rounded-lg
                       resize-none focus:outline-none focus:border-accent text-text-primary
                       placeholder:text-text-tertiary"
              rows={2}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function SomedayReview({
  somedayItems,
  goals,
  reflection,
  onReflectionChange,
  onActivateSomeday,
  onDeleteSomeday
}) {
  const [showAllSomeday, setShowAllSomeday] = useState(false);
  const displayedSomeday = showAllSomeday ? somedayItems : somedayItems.slice(0, 5);
  const activeGoals = goals.filter(g => g.status === 'active' || !g.status);

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-monthly/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles size={32} className="text-monthly" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">Someday & Goals</h2>
          <p className="text-text-secondary">
            Review your backlog and goals. Anything ready to activate?
          </p>
        </div>

        {/* Someday/Maybe section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-primary">Someday/Maybe</h3>
            <span className="text-xs text-text-tertiary">{somedayItems.length} items</span>
          </div>

          {somedayItems.length === 0 ? (
            <div className="p-4 bg-surface-card rounded-xl border border-border text-center">
              <p className="text-sm text-text-tertiary">No someday items</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {displayedSomeday.map(item => (
                  <SomedayItem
                    key={item.id}
                    item={item}
                    onActivate={onActivateSomeday}
                    onDelete={onDeleteSomeday}
                  />
                ))}
              </div>
              {somedayItems.length > 5 && (
                <button
                  onClick={() => setShowAllSomeday(!showAllSomeday)}
                  className="w-full mt-2 py-2 text-xs text-text-tertiary hover:text-text-secondary
                           bg-transparent border-none cursor-pointer"
                >
                  {showAllSomeday ? 'Show less' : `Show ${somedayItems.length - 5} more`}
                </button>
              )}
            </>
          )}
        </div>

        {/* Goals section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-primary">Active Goals</h3>
            <span className="text-xs text-text-tertiary">{activeGoals.length} goals</span>
          </div>

          {activeGoals.length === 0 ? (
            <div className="p-4 bg-surface-card rounded-xl border border-border text-center">
              <p className="text-sm text-text-tertiary">No active goals</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activeGoals.map(goal => (
                <GoalCard key={goal.id} goal={goal} />
              ))}
            </div>
          )}
        </div>

        {/* Reflection section */}
        <ReflectionSection
          reflection={reflection}
          onReflectionChange={onReflectionChange}
        />

        {/* Tips */}
        <div className="mt-6 p-4 bg-surface-hover/50 rounded-xl">
          <p className="text-sm text-text-tertiary">
            <strong className="text-text-secondary">Tip:</strong> If a someday item has been sitting
            for months without excitement, consider deleting it. If it's important, you'll add it again.
          </p>
        </div>
      </div>
    </div>
  );
}
